import React from 'react';
import { Clock, Calendar } from 'lucide-react';

interface DelayStepConfigProps {
  config: any;
  onChange: (config: any) => void;
}

const DELAY_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
];

const WEEKDAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
];

export const DelayStepConfig: React.FC<DelayStepConfigProps> = ({ config, onChange }) => {
  const delayType = config.delay_type || 'fixed';

  const calculateTotalTime = () => {
    if (delayType !== 'fixed') return '';
    
    const amount = parseInt(config.delay_amount) || 0;
    const unit = config.delay_unit || 'hours';
    
    if (amount === 0) return '';
    
    switch (unit) {
      case 'minutes': {
        if (amount < 60) return `${amount} minute${amount !== 1 ? 's' : ''}`;
        const hours = Math.floor(amount / 60);
        const mins = amount % 60;
        return mins > 0
          ? `${hours} hour${hours !== 1 ? 's' : ''} ${mins} min`
          : `${hours} hour${hours !== 1 ? 's' : ''}`;
      }
      case 'hours': {
        if (amount < 24) return `${amount} hour${amount !== 1 ? 's' : ''}`;
        const days = Math.floor(amount / 24);
        const hrs = amount % 24;
        return hrs > 0
          ? `${days} day${days !== 1 ? 's' : ''} ${hrs} hour${hrs !== 1 ? 's' : ''}`
          : `${days} day${days !== 1 ? 's' : ''}`;
      }
      case 'days': {
        if (amount < 7) return `${amount} day${amount !== 1 ? 's' : ''}`;
        const weeks = Math.floor(amount / 7);
        const dys = amount % 7;
        return dys > 0
          ? `${weeks} week${weeks !== 1 ? 's' : ''} ${dys} day${dys !== 1 ? 's' : ''}`
          : `${weeks} week${weeks !== 1 ? 's' : ''}`;
      }
      case 'weeks':
        return `${amount} week${amount !== 1 ? 's' : ''}`;
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Delay Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Delay Type *
        </label>
        <div className="space-y-2">
          <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="delay_type"
              value="fixed"
              checked={delayType === 'fixed'}
              onChange={(e) => onChange({ ...config, delay_type: e.target.value })}
              className="mt-1 text-blue-600 focus:ring-blue-500"
            />
            <div className="ml-3">
              <div className="font-medium text-sm">Fixed Delay</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Wait a specific amount of time before continuing
              </div>
            </div>
          </label>
          <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="delay_type"
              value="until_time"
              checked={delayType === 'until_time'}
              onChange={(e) => onChange({ ...config, delay_type: e.target.value })}
              className="mt-1 text-blue-600 focus:ring-blue-500"
            />
            <div className="ml-3">
              <div className="font-medium text-sm">Until Specific Time</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Wait until a specific time of day before continuing
              </div>
            </div>
          </label>
          <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="delay_type"
              value="until_day"
              checked={delayType === 'until_day'}
              onChange={(e) => onChange({ ...config, delay_type: e.target.value })}
              className="mt-1 text-blue-600 focus:ring-blue-500"
            />
            <div className="ml-3">
              <div className="font-medium text-sm">Until Specific Day</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Wait until a specific day of the week before continuing
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Fixed Delay Configuration */}
      {delayType === 'fixed' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delay Amount *
              </label>
              <input
                type="number"
                value={config.delay_amount || ''}
                onChange={(e) => onChange({ ...config, delay_amount: e.target.value })}
                min="1"
                placeholder="Enter amount..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit *
              </label>
              <select
                value={config.delay_unit || 'hours'}
                onChange={(e) => onChange({ ...config, delay_unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {DELAY_UNITS.map(unit => (
                  <option key={unit.value} value={unit.value}>
                    {unit.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {calculateTotalTime() && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <div className="text-sm font-medium text-blue-900">Total Delay</div>
                <div className="text-xs text-blue-700">{calculateTotalTime()}</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Until Time Configuration */}
      {delayType === 'until_time' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Time *
          </label>
          <input
            type="time"
            value={config.until_time || '09:00'}
            onChange={(e) => onChange({ ...config, until_time: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-2 text-xs text-gray-500">
            Workflow will continue at this time. If this time has already passed today, it will wait until tomorrow.
          </p>
        </div>
      )}

      {/* Until Day Configuration */}
      {delayType === 'until_day' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Day of Week *
            </label>
            <select
              value={config.until_day || 'monday'}
              onChange={(e) => onChange({ ...config, until_day: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {WEEKDAYS.map(day => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              At Time
            </label>
            <input
              type="time"
              value={config.until_day_time || '09:00'}
              onChange={(e) => onChange({ ...config, until_day_time: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-2 text-xs text-gray-500">
              Workflow will continue on the specified day at this time
            </p>
          </div>
        </>
      )}

      {/* Business Hours Settings */}
      <div className="border-t pt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Business Hours Settings</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.respect_business_hours || false}
              onChange={(e) => onChange({ ...config, respect_business_hours: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Only continue during business hours</span>
          </label>
          {config.respect_business_hours && (
            <div className="ml-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Business Hours Start
                </label>
                <input
                  type="time"
                  value={config.business_hours_start || '08:00'}
                  onChange={(e) => onChange({ ...config, business_hours_start: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Business Hours End
                </label>
                <input
                  type="time"
                  value={config.business_hours_end || '17:00'}
                  onChange={(e) => onChange({ ...config, business_hours_end: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Weekend Settings */}
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.skip_weekends || false}
            onChange={(e) => onChange({ ...config, skip_weekends: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Skip weekends</span>
        </label>
        <p className="text-xs text-gray-500 ml-6">
          If the delay ends on a weekend, the workflow will continue on the next Monday
        </p>
      </div>

      {/* Timezone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Timezone
        </label>
        <select
          value={config.timezone || 'America/New_York'}
          onChange={(e) => onChange({ ...config, timezone: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {TIMEZONES.map(tz => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          All times will be interpreted in this timezone
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Calendar className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-600">
            <p className="font-medium mb-1">Delay Best Practices</p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Consider customer time zones when scheduling delays</li>
              <li>Avoid sending during late night or early morning hours</li>
              <li>Space out communications to prevent overwhelming contacts</li>
              <li>Test delays with short durations before using long ones</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
